import React, {useContext, useEffect, useState} from "react";
import {
    chartAxisFormatEnums as defaultFormatters,
    dataset,
    datasetsEnums,
    DynamicChart,
    formatFunction,
    Grid,
    SmartSelect,
    Typography,
    Skeleton
} from "@leap4/frontend";

import {
    getDatasetsForGraph,
    getLabelsForGraph,
    getXAxisFormatFunc,
    graphDataProgressions,
    indexToTimeInterval,
    TimeDisplay
} from "./charts/charts";
import moment from "moment";
import {EntitiesProperties} from "../../../utils/enums";
import {UserViewsContext} from "../../../contexts/userViewsContext";

const {
    COST,
    IMPRESSIONS,
    PROFIT,
    REVENUE,
    REQUESTS,
    NET_COST,
    NET_REVENUE,
    NET_PROFIT,
    COMPLETION,
    VCR,
    FILL_RATE,
    CPM,
    RPM,
    INCOMING_REQUESTS,
    AD_FILL_RATE,
    AD_REQUESTS,
    SERVICE_COST
} = EntitiesProperties.NUMERIC.ALL
const allOptionalCompares = [
    {name: 'Net Profit', value: NET_PROFIT},
    {name: 'Profit', value: PROFIT},
    {name: 'Net Revenue', value: NET_REVENUE},
    {name: 'Revenue', value: REVENUE},
    {name: 'Net Cost', value: NET_COST},
    {name: 'Cost', value: COST},
    {name: 'Impressions', value: IMPRESSIONS},
    {name: 'Ad Requests', value: REQUESTS},
    {name: 'Completion', value: COMPLETION},
    {name: 'VCR', value: VCR},
    {name: 'Ad Fill Rate', value: AD_FILL_RATE},
    {name: 'Fill Rate', value: FILL_RATE},
    {name: 'CPM', value: CPM},
    {name: 'RPM', value: RPM},
    {name: 'Incoming Requests', value: INCOMING_REQUESTS},
    {name: 'Serving Cost', value: SERVICE_COST}
]


export interface IOverviewGraphResponse {
    selectedDates: IOverviewGraphDataNew
    dayAgo: IOverviewGraphDataNew,
    weekAgo: IOverviewGraphDataNew
    monthAgo: IOverviewGraphDataNew,
    specificComparisonDate?: IOverviewGraphDataNew
}

export interface IComparisonGraphProps {
    isLoading: boolean;
    graphData: IOverviewGraphResponse
    startDate: string;
    endDate: string;
    setNewInterval: (interval: number) => void
    graphIntervalIndx: number
    tension?: number,
}

type axisFormatType = formatFunction | defaultFormatters

export interface graphDataTillMidnightNew {
    id?: string
    time: Date
    totals: {
        revenue: number;
        cost: number;
        impressions: number;
        requests: number;
    }
}


export interface IOverviewGraphDataNew {
    data: Array<graphDataTillMidnightNew>;
}

export default function ComparisonOverviewGraph(props: IComparisonGraphProps) {
    const {
        isLoading,
        startDate,
        endDate,
        graphData,
        setNewInterval,
        graphIntervalIndx,
        tension = 0.5,
    } = props
    const {userViews} = useContext(UserViewsContext)
    const [optionalCompares, setOptionalCompares] = useState([])
    const [labelsForGraph, setLabelsForGraph] = useState<string[]>(null)
    const [selectedGraphDataProgressionIdx, setGraphDataProgressionIdx] = useState(0);
    const [graphDataType, setGraphDataType] = useState<{ name: string, value: datasetsEnums }[]>(graphDataProgressions)
    const [selectedCompareIdx, setSelectedCompareIdx] = useState(0);
    const [datasets, setDatasets] = useState([])
    const [xAxisFormat, setXAxisFormat] = useState<axisFormatType>(null)
    const [yAxisFormat, setYAxisFormat] = useState<axisFormatType>(defaultFormatters.USD)

    const generateChartProps = () => {
        if (!graphData) return;
        const timeInterval = indexToTimeInterval(graphIntervalIndx)
        const labels = getLabelsForGraph(startDate, endDate, timeInterval)

        setLabelsForGraph(labels)

        const xAxisFormat = getXAxisFormatFunc(startDate, endDate, timeInterval);
        setXAxisFormat(prevState => xAxisFormat)
        generateDatasetsAndAxisFormat()
    }

    useEffect(generateChartProps, [graphData, selectedCompareIdx, selectedGraphDataProgressionIdx, graphIntervalIndx]);

    useEffect(() => {
        const {graphViews} = userViews.activePreset
        setOptionalCompares(getOptionalComparesByViews(graphViews))
    }, [userViews])

    const getOptionalComparesByViews = (graphViews) => {
        return graphViews?.map(view => {
            return allOptionalCompares.find(comp => {
                return comp.value === view
            })
        }) || []
    }
    const generateDatasetsAndAxisFormat = () => {
        if (!graphData) {
            return;
        }
        const type = graphDataProgressions[selectedGraphDataProgressionIdx].value;
        const intervalType = indexToTimeInterval(graphIntervalIndx);
        let currDatasets: dataset[] = []
        const selectedEntity = optionalCompares[selectedCompareIdx]?.value
        switch (selectedEntity) {
            case COST:
            case PROFIT:
            case REVENUE:
            case NET_REVENUE:
            case NET_COST:
            case NET_PROFIT:
                setGraphDataType(graphDataProgressions)
                currDatasets = getDatasetsForGraph(graphData, selectedEntity, intervalType, type, graphIntervalIndx)
                setYAxisFormat(defaultFormatters.USD)
                break;
            case IMPRESSIONS:
            case COMPLETION:
            case REQUESTS:
            case AD_REQUESTS:
                setGraphDataType(graphDataProgressions)
                currDatasets = getDatasetsForGraph(graphData, selectedEntity, intervalType, type, graphIntervalIndx);
                setYAxisFormat(defaultFormatters.NUMBER)
                break;
            case VCR:
            case FILL_RATE:
            case AD_FILL_RATE:
                setGraphDataType([{name: 'Absolute', value: datasetsEnums.Actual}])
                currDatasets = getDatasetsForGraph(graphData, selectedEntity, intervalType, 'Absolute', graphIntervalIndx);
                setYAxisFormat(defaultFormatters.NUMBER)
                break;
            case CPM:
            case RPM:
                setGraphDataType([{name: 'Absolute', value: datasetsEnums.Actual}])
                currDatasets = getDatasetsForGraph(graphData, selectedEntity, intervalType, 'Absolute', graphIntervalIndx);
                setYAxisFormat(defaultFormatters.USD)
                break;
            default:
                setGraphDataType(graphDataProgressions)
                currDatasets = getDatasetsForGraph(graphData, selectedEntity, intervalType, type, graphIntervalIndx);
                setYAxisFormat(defaultFormatters.NUMBER)
                break;
        }
        setDatasets(currDatasets)
    }

    const getBucketsForLabels = () => {
        return moment(startDate).isSame(endDate, 'day') && moment(startDate).isSame(moment(), 'day') ? graphData.dayAgo.data : graphData.selectedDates.data
    }


    if (isLoading || !optionalCompares) {
        return <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} height={"100%"} animation={"wave"}/>
    }

    if (!graphData) {
        return <Typography variant={"overline"}>No Data in the selected date range</Typography>
    } 
    if(!labelsForGraph || datasets.length == 0){
        generateChartProps();
    }
    return (
        <>
            <DynamicChart
                style={{borderRadius: 16, boxShadow: 'none'}}
                tension={tension}
                datasets={datasets} labels={labelsForGraph} axisFormat={{x: xAxisFormat, y: yAxisFormat}}
                title={"Comparison Graph"}>
                <Grid container gap={'15px'} wrap={"nowrap"}>
                    <Grid item>
                        <SmartSelect
                            selectProps={{size: "small"}}
                            items={optionalCompares}
                            selectedItemInd={selectedCompareIdx}
                            setSelectedState={(val) => setSelectedCompareIdx(val)}
                            title={"Comparsion"}/>
                    </Grid>

                    <Grid item>
                        <SmartSelect
                            selectProps={{size: "small"}}
                            items={graphDataType}
                            selectedItemInd={selectedGraphDataProgressionIdx}
                            setSelectedState={(val) => setGraphDataProgressionIdx(val)}
                            title={"Data Type"}/>
                    </Grid>

                    <Grid item marginLeft={"auto"} marginRight={"35px"}>
                        <SmartSelect
                            selectProps={{size: "small"}}
                            items={TimeDisplay}
                            selectedItemInd={graphIntervalIndx}
                            setSelectedState={setNewInterval}
                            title={"Time Interval"}/>
                    </Grid>
                </Grid>
            </DynamicChart>
        </>

    )
}

